import { router } from '@inertiajs/react'
import { t } from '@/shared/lib/i18n'
import { Button } from '@/shared/ui/button'
import { reportsMonthlyShowPath } from '@/shared/lib/routes'
import ReportStatusBadge from './ReportStatusBadge'
import type WebInstrumentTaskReport from '@/shared/types/web/WebInstrumentTaskReport'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface Props {
  record: WebInstrumentTaskReport
}

const ReportRow = ({ record }: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const compliancePercent = record.compliancePercent != null ? `${record.compliancePercent}%` : '—'
  const showHref = reportsMonthlyShowPath({ id: record.id })
  // Live / Not ready (month in progress) rows use View Now; closed-month statuses keep View Report.
  const viewLabel = record.derivedStatus?.live
    ? t('approvals.viewNow')
    : t('reports.monthly.viewReport')

  const updatedAt = record.updatedAt ? formatDateTime(record.updatedAt, record.timeZoneName) : '—'

  const goToReport = () => {
    router.visit(showHref)
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-md border p-3 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between sm:gap-3 cursor-pointer"
      role="link"
      tabIndex={0}
      aria-label={`${viewLabel}: ${record.departmentName ?? ''}`}
      onClick={goToReport}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          goToReport()
        }
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">{record.departmentName ?? '—'}</span>
          <span className="text-sm text-muted-foreground">{record.siteName ?? '—'}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <ReportStatusBadge record={record} />

          <span className="text-sm">
            <span className="text-muted-foreground">
              {t('reports.monthly.compliancePercent')}:{' '}
            </span>
            {compliancePercent}
          </span>

          {record.isSigned && (
            <span className="text-xs font-medium text-green-600">
              {t('reports.monthly.signed')}
            </span>
          )}

          <span className="text-xs text-muted-foreground">
            <span>{t('reports.monthly.lastModified')}: </span>
            {updatedAt}
          </span>
        </div>
      </div>

      <div
        className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-center"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <a href={showHref} onClick={(event) => event.stopPropagation()}>
          <Button variant="outline" size="sm">
            {viewLabel}
          </Button>
        </a>
      </div>
    </div>
  )
}

export default ReportRow
